import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Box } from '@mui/material';
import type { Participation } from 'app/types';
import { getDisplayDateValue } from 'app/utils';
import DetailsPanelRow from 'app/components/DetailsPanelRow';
import {
  ParticipationTranslationKeys,
  PARTICIPATION_TYPE_TRANSLATION_KEY,
  REASON_START_TRANSLATION_KEY,
  REASON_END_TRANSLATION_KEY,
} from '../../EmployeeIncomeParticipations.types';

type ParticipationsBodyProps = {
  participations: Participation[];
  expanded: boolean[];
  loading: boolean;
  disabledEditButton: boolean;
  disabledDeleteButton: boolean;
  onExpand: ({ id, expanded }: { id: number; expanded: boolean }) => void;
  onEditButtonClick: (participation: Participation) => () => void;
  onDeleteButtonClick: (participation: Participation) => () => void;
};

export const ParticipationsBody: React.FC<ParticipationsBodyProps> = ({
  participations,
  expanded,
  loading,
  disabledEditButton,
  disabledDeleteButton,
  onExpand,
  onEditButtonClick,
  onDeleteButtonClick,
}) => {
  return (
    <>
      {participations.map((participation, idx) => {
        const isExpanded = expanded[idx] ?? false;

        const rows: Record<string, React.ReactNode> = {
          [ParticipationTranslationKeys.startDate]: getDisplayDateValue(participation.startDate),
          [ParticipationTranslationKeys.type]: (
            <FormattedMessage id={PARTICIPATION_TYPE_TRANSLATION_KEY[participation.type]} />
          ),
          [ParticipationTranslationKeys.reasonStart]: (
            <FormattedMessage id={REASON_START_TRANSLATION_KEY[participation.reasonStart]} />
          ),
        };

        if (participation.endDate) {
          rows[ParticipationTranslationKeys.endDate] = getDisplayDateValue(participation.endDate);
        }

        if (participation.reasonEnd) {
          rows[ParticipationTranslationKeys.reasonEnd] = (
            <FormattedMessage id={REASON_END_TRANSLATION_KEY[participation.reasonEnd]} />
          );
        }

        const title = participation.endDate ? (
          <>
            {getDisplayDateValue(participation.startDate)}
            {' - '}
            {getDisplayDateValue(participation.endDate)}
          </>
        ) : (
          getDisplayDateValue(participation.startDate)
        );

        return (
          <Box key={participation.mongoId} sx={{ width: '100%' }}>
            <DetailsPanelRow
              title={title}
              expanded={isExpanded}
              rows={rows}
              loading={loading}
              id={idx}
              onExpand={onExpand}
              onEditButtonClick={participation.endDate ? undefined : onEditButtonClick(participation)}
              onDeleteButtonClick={onDeleteButtonClick(participation)}
              disabledEditButton={disabledEditButton}
              disabledDeleteButton={disabledDeleteButton}
            />
          </Box>
        );
      })}
    </>
  );
};
